// Utils
import { getCookie } from "./cookies";
import fetch from "./fetch";
import { getStoredUser } from "./currentUser";
import { captureError } from "./error";
import { createWrapper, resolveWhen } from "@segment/analytics-consent-tools";

export const updateUserPreferences = () => {
  const preferencesCookie = getCookie("tracking-preferences");
  const currentUser = getStoredUser();

  if (currentUser && currentUser.id) {
    fetch(`users/${currentUser.id}/tracking_preferences`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ segment_integrations: preferencesCookie }),
    }).catch((error) => captureError(error, "Error in updateUserPreferences"));
  }
};

// Map DataGrail Categories to Segment Categories
export const normalizeCategories = (categories) => {
  // Handle preferences object format from callback
  if (
    categories &&
    categories.consentPreferences &&
    categories.consentPreferences.cookieOptions
  ) {
    categories = categories.consentPreferences.cookieOptions;
  }

  if (!Array.isArray(categories)) {
    return {};
  }

  const DG_TO_SEGMENT_CATEGORY_ID = {
    "dg-category-marketing": "Advertising",
    "dg-category-performance": "Analytics",
    "dg-category-essential": "Essential",
    "dg-category-functional": "Functional",
  };

  const segmentPrefs = {};

  categories.forEach(function (opt) {
    const categoryId = DG_TO_SEGMENT_CATEGORY_ID[opt.gtm_key];
    const isEnabled = !!opt.isEnabled;
    if (categoryId) segmentPrefs[categoryId] = isEnabled;
    if (categoryId === "Analytics") segmentPrefs["Session Replay"] = isEnabled;
  });

  return segmentPrefs;
};

export const withCMP = createWrapper({
  // Wait to load wrapper or call "shouldLoadSegment" until window.CMP exists.
  shouldLoadWrapper: async () => {
    await resolveWhen(() => window.DG_BANNER_API !== undefined, 500);
  },

  // Allow for control over wrapper + analytics initialization.
  // Delay any calls to analytics.load() until this function returns / resolves.
  shouldLoadSegment: async (ctx) => {
    const config = window.DG_BANNER_API.config();
    const consentModel = config.consentMode;
    const categories = window.DG_BANNER_API.getCategories();

    if (consentModel === "optout") {
      return ctx.load({ consentModel: "opt-out" });
    } else {
      await resolveWhen(() => !config.showBanner && categories.length, 500);
      return ctx.load({ consentModel: "opt-in" });
    }
  },

  getCategories: () => {
    return normalizeCategories(window.DG_BANNER_API.getCategories());
  },

  registerOnConsentChanged: (setCategories) => {
    // Initialize if it doesn't exist (in case HTML script hasn't run yet)
    if (!window.__segmentCategoryPreferences) {
      window.__segmentCategoryPreferences = {};
    }

    // Set initial categories from current value
    const initialPrefs = window.__segmentCategoryPreferences;
    if (initialPrefs && Object.keys(initialPrefs).length > 0) {
      setCategories({ ...initialPrefs });
    }

    // Store the actual value in a closure
    let storedValue = { ...(window.__segmentCategoryPreferences || {}) };

    // Listen for changes to window.__segmentCategoryPreferences
    // Replace it with a property descriptor that intercepts assignments
    Object.defineProperty(window, "__segmentCategoryPreferences", {
      get() {
        return storedValue;
      },
      set(newValue) {
        // Update the stored value
        storedValue = newValue || {};
        // Trigger setCategories with the updated preferences
        setCategories({ ...storedValue });
      },
      enumerable: true,
      configurable: true,
    });
  },
});
